import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";
import { useId } from "react";

export type StandardDialogKind = "alert" | "confirm";

export interface StandardDialogProps {
    open: boolean;
    title?: ReactNode;
    children?: ReactNode;
    onConfirm: () => void;
    onCancel: () => void;
    confirmText?: string;
    cancelText?: string;
    kind?: StandardDialogKind; // alert -> red confirm, confirm -> primary confirm
    icon?: ReactNode; // optional colored icon for alert/confirmation
    maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
    fullWidth?: boolean;
    confirmDisabled?: boolean;
    loading?: boolean;
    keepMounted?: boolean;
    contentSx?: SxProps<Theme>;
}

export function ConfirmationDialog({
    open,
    title,
    children,
    onConfirm,
    onCancel,
    confirmText = "Confirm",
    cancelText = "Cancel",
    kind = "confirm",
    icon,
    maxWidth = "sm",
    fullWidth = true,
    confirmDisabled,
    loading,
    keepMounted,
    contentSx,
}: StandardDialogProps) {
    const titleId = useId();

    const confirmColor = kind === "alert" ? "error" : "primary";

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            aria-labelledby={titleId}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            keepMounted={keepMounted}
        >
            {title ? (
                <DialogTitle id={titleId}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        {icon ? <Box aria-hidden>{icon}</Box> : null}
                        <Typography
                            component="span"
                            variant="h6"
                            fontWeight={700}
                        >
                            {title}
                        </Typography>
                    </Stack>
                </DialogTitle>
            ) : null}
            <Divider />
            {children ? (
                <DialogContent sx={contentSx}>{children}</DialogContent>
            ) : null}
            <Divider />
            <DialogActions sx={{ px: 3, py: 2.25 }}>
                <Button onClick={onCancel} variant="text" color="inherit">
                    {cancelText}
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color={confirmColor}
                    disabled={confirmDisabled || loading}
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ConfirmationDialog;
